import { Button, Input } from '@components/common';
import React, { useState } from 'react';

const MAX_RECIPIENTS = 5;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface BillEmailPanelProps {
  billNumber: number;
  defaultRecipient: string;
  /** Resolves to an error message to show inline, or null on success. */
  onSend: (recipients: string[]) => Promise<string | null>;
  onCancel: () => void;
}

const parseRecipients = (value: string): string[] =>
  Array.from(
    new Set(
      value
        .split(/[,;\s]+/)
        .map((email) => email.trim())
        .filter(Boolean),
    ),
  );

const validateRecipients = (recipients: string[]): string | null => {
  if (recipients.length === 0) return 'Enter an email address.';
  if (recipients.length > MAX_RECIPIENTS) {
    return `Send to at most ${MAX_RECIPIENTS} addresses at a time.`;
  }
  const invalid = recipients.find((email) => !EMAIL_PATTERN.test(email));
  return invalid ? `"${invalid}" is not a valid email address.` : null;
};

/** Inline "send this bill to…" strip shown inside the bill modal. */
export const BillEmailPanel: React.FC<BillEmailPanelProps> = ({
  billNumber,
  defaultRecipient,
  onSend,
  onCancel,
}) => {
  const [value, setValue] = useState(defaultRecipient);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const recipients = parseRecipients(value);
    const validationError = validateRecipients(recipients);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setIsSending(true);
    const sendError = await onSend(recipients);
    setIsSending(false);
    if (sendError) setError(sendError);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mb-5 rounded-xl border border-slate-200 bg-slate-50 p-4"
      noValidate
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="min-w-0 flex-1">
          <Input
            id="bill-email-recipients"
            type="text"
            inputMode="email"
            dense
            label={`Send bill #${billNumber} to`}
            value={value}
            onChange={(next) => {
              setValue(next);
              if (error) setError(null);
            }}
            disabled={isSending}
            autoComplete="email"
            placeholder="name@example.com"
            helperText={`The bill is attached as a PDF. Separate up to ${MAX_RECIPIENTS} addresses with commas.`}
            error={error ?? undefined}
          />
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:pt-6">
          <Button
            type="button"
            variant="text"
            size="md"
            onClick={onCancel}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button type="submit" size="md" disabled={isSending}>
            {isSending ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </div>
    </form>
  );
};
